import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  newBookingSchema,
  BOOKING_TYPES,
  type NewBookingFormValues,
  type NewBookingFormInput,
} from '@/utils/schemas'

interface BookingFormProps {
  defaultValues?: Partial<NewBookingFormInput>
  onSubmit: (values: NewBookingFormValues) => void
  onCancel?: () => void
  isPending: boolean
  isError: boolean
  submitLabel: string
}

export function BookingForm({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
  isError,
  submitLabel,
}: BookingFormProps) {
  const { t } = useTranslation()
  const { register, handleSubmit } = useForm<NewBookingFormInput, unknown, NewBookingFormValues>({
    resolver: zodResolver(newBookingSchema),
    defaultValues,
  })

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-3 rounded-md border border-line-strong bg-panel-2 p-4"
    >
      <select
        aria-label={t('bookings.selectType')}
        className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
        {...register('type')}
      >
        {BOOKING_TYPES.map((type) => (
          <option key={type} value={type}>
            {t(`bookings.type.${type}`)}
          </option>
        ))}
      </select>
      <input
        aria-label={t('bookings.reference')}
        placeholder={t('bookings.reference')}
        className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
        {...register('reference')}
      />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block font-mono text-[10px] text-brass-dim uppercase">
            {t('bookings.startAt')}
          </label>
          <input
            type="datetime-local"
            aria-label={t('bookings.startAt')}
            className="w-full rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('startAt')}
          />
        </div>
        <div>
          <label className="mb-1 block font-mono text-[10px] text-brass-dim uppercase">
            {t('bookings.endAt')}
          </label>
          <input
            type="datetime-local"
            aria-label={t('bookings.endAt')}
            className="w-full rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
            {...register('endAt')}
          />
        </div>
      </div>
      {isError && (
        <p role="alert" className="text-sm text-brass-warm">
          {t('bookings.saveError')}
        </p>
      )}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory"
          >
            {t('bookings.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={isPending}
          className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
